import cn from 'clsx'
import React, { useEffect, useState } from 'react'
import { useDrop } from 'react-dnd'
import ReactDOM from 'react-dom'

import mixpanel from '@disco/api/mixpanel'
import { TRACK_NESTING } from '@disco/constants/FeatureTypes'
import { CUSTOM_CHANNEL, INBOX_TYPES } from '@disco/constants/GroupTypes'
import { FILE, GROUP, PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import {
  addAggregatedTrackToDisco,
  alterPlaylistGroups,
  alterTrackGroups,
  copyPlaylistToChannel,
} from '@disco/state-management/actions/ExplorerActions'
import { updateGroup } from '@disco/state-management/actions/GroupActions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import { uploadFilesToGroups } from '@disco/state-management/actions/upload-actions'
import { saveUserSortedChannels } from '@disco/state-management/actions/UserActions'
import { makeIsFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getGroups,
  getUserSortedChannels,
  makeGetGroupLocks,
} from '@disco/state-management/selectors/groups'
import { getAllSelectedItems } from '@disco/state-management/selectors/selection'
import {
  sendPlaylistDownloadEventThrottled,
  sendTrackDownloadEventThrottled,
} from '@disco/utils/eventsApi'
import { getFilesFromDraggedItem } from '@disco/utils/file-utils'
import { getFolderChannel } from '@disco/utils/groups'
import { getMixpanelDragAndDropProps } from '@disco/utils/mixpanel/drop-tracks'
import moveFromTo from '@disco/utils/moveFromTo'
import { isReadOnlyPlaylist } from '@disco/utils/playlists'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'
import { isReadOnlyTrack } from '@disco/utils/tracks'

import type { Group } from 'types/group'

import { dndConnectRef } from 'utils/dnd-connect-ref'

import { DropFilesIndicator } from 'components/ui/drop-indicator'

import { useDispatch, useSelector } from 'hooks/use-redux'
import useTimeout from 'hooks/use-timeout'

type Props = {
  group: Group
  enableGroupDrop?: boolean | null
  children: React.ReactNode
  onFileHover: () => void
}

const appNode =
  typeof document !== 'undefined' ? document.getElementById('app') : null

const getGroupsToRemove = (
  groups: Group[],
  sourceGroup: Group | null | undefined,
) => {
  const parentChannel = sourceGroup
    ? getFolderChannel(groups, sourceGroup)
    : null
  return sourceGroup &&
    sourceGroup.group_type === CUSTOM_CHANNEL &&
    !parentChannel?.lock_content
    ? [sourceGroup]
    : []
}

const getSourceGroup = (
  groups: Group[],
  draggedItem: any,
): Group | undefined => {
  if (draggedItem?.sourceGroupId) {
    return groups.find(g => g.id === draggedItem.sourceGroupId)
  }
  return undefined
}

export default function CustomGroupDropWrapper({
  group,
  enableGroupDrop,
  children,
  onFileHover,
}: Props) {
  const dispatch = useDispatch()
  const groups = useSelector(getGroups)
  const selectedItems = useSelector(getAllSelectedItems)
  const userSortedChannels = useSelector(getUserSortedChannels)
  const restrictions = useSelector(state => {
    const getGroupLocks = makeGetGroupLocks()
    return getGroupLocks(state, { groupId: group.id })
  })
  const nestingEnabled = useSelector(state => {
    const isFeatureEnabled = makeIsFeatureEnabled()
    return isFeatureEnabled(state, { featureName: TRACK_NESTING })
  })

  const { lock_structure, lock_content } = restrictions ?? {}

  const [{ isOver, canDrop, draggedItem }, dropRef] = useDrop<
    any,
    unknown,
    { isOver: boolean; canDrop: boolean; draggedItem: any }
  >(
    () => ({
      accept: [TRACK, PLAYLIST, GROUP, FILE],
      canDrop: item => {
        if (group?.permissions) {
          if (!group.permissions.content && !group.permissions.share_link) {
            return false
          }
        }
        if (
          (!nestingEnabled && item?.files) ||
          (lock_structure && item?.group)
        ) {
          return false
        }

        if (lock_content && (item?.track || item?.playlist || item?.files)) {
          return false
        }

        if (item?.track) {
          return Boolean(item.track.uploaded)
        }

        const isDraggedChannel = !item?.group?.parent
        const willDropOnFolder = group?.parent

        if (item?.group && isDraggedChannel && willDropOnFolder) {
          return false
        }

        return true
      },
      drop: (draggedItem, monitor) => {
        if (!draggedItem || monitor.didDrop()) return

        const readOnlyTrack = isReadOnlyTrack(draggedItem.track)
        const readOnlyPlaylist = isReadOnlyPlaylist(draggedItem.playlist)
        const partOfSelection = isDraggedItemPartOfSelection(
          selectedItems,
          draggedItem,
        )
        const sourceGroup = getSourceGroup(groups, draggedItem)
        const targetParent = getFolderChannel(groups, group)
        const sourceParent = getFolderChannel(groups, sourceGroup)
        const removeGroups =
          targetParent?.is_auto_tagged && targetParent?.id !== sourceParent?.id
            ? []
            : getGroupsToRemove(groups, sourceGroup)
        const targetGroups = [group]

        ;(async () => {
          let effectiveTrack = draggedItem.track
          if (readOnlyTrack) {
            const includeNestedTracks = Boolean(sourceGroup)
            effectiveTrack = await dispatch(
              addAggregatedTrackToDisco({
                track: draggedItem.track,
                originGroupId: sourceGroup?.id,
                includeNestedTracks,
                includeStems: includeNestedTracks,
              }),
            )
          }

          if (readOnlyPlaylist) {
            await dispatch(
              copyPlaylistToChannel(
                draggedItem.playlist,
                targetGroups[0],
                sourceGroup?.id,
              ),
            )
            dispatch(resetSelection())
            return
          }

          if (sourceGroup?.id === group.id) {
            return
          }

          if (selectedItems.length > 0 && partOfSelection) {
            const playlists = selectedItems.filter(
              item => item.type === PLAYLIST,
            )
            if (playlists.length > 0) {
              dispatch(
                alterPlaylistGroups(playlists, targetGroups, removeGroups),
              )
            }

            const tracks = selectedItems.filter(
              item =>
                item.type === TRACK && !(item as any).outside_your_territories,
            )
            if (tracks.length > 0) {
              dispatch(alterTrackGroups(tracks, targetGroups, removeGroups))
            }

            selectedItems.forEach(item => {
              const fromInbox =
                draggedItem.sourceGroupType &&
                INBOX_TYPES.includes(draggedItem.sourceGroupType)

              if (fromInbox && item.type === PLAYLIST) {
                playlists.forEach(playlist => {
                  sendPlaylistDownloadEventThrottled(
                    (playlist as any).playlist_id,
                    undefined,
                    draggedItem.sourceGroupId,
                  )
                })
              } else if (
                fromInbox &&
                item.type === TRACK &&
                !(item as any).outside_your_territories
              ) {
                sendTrackDownloadEventThrottled((item as any).id, 'in-app', {
                  group_id: draggedItem.sourceGroupId,
                  playlist_id: draggedItem.sourcePlaylistId,
                })
              }
            })
          } else if (
            effectiveTrack &&
            effectiveTrack.uploaded &&
            !effectiveTrack.outside_your_territories
          ) {
            dispatch(
              alterTrackGroups([effectiveTrack], targetGroups, removeGroups),
            )

            if (
              draggedItem.sourceGroupType &&
              INBOX_TYPES.includes(draggedItem.sourceGroupType)
            ) {
              sendTrackDownloadEventThrottled(effectiveTrack.id, 'in-app', {
                group_id: draggedItem.sourceGroupId,
                playlist_id: draggedItem.sourcePlaylistId,
              })
            }
          } else if (draggedItem.playlist) {
            dispatch(
              alterPlaylistGroups(
                [draggedItem.playlist],
                targetGroups,
                removeGroups,
              ),
            )

            if (
              draggedItem.sourceGroupType &&
              INBOX_TYPES.includes(draggedItem.sourceGroupType)
            ) {
              sendPlaylistDownloadEventThrottled(
                draggedItem.playlist.playlist_id,
                undefined,
                draggedItem.sourceGroupId,
              )
            }
          } else if (draggedItem.items) {
            getFilesFromDraggedItem(draggedItem).then((files: File[]) => {
              // TODO: Remove this check and handle file limits at a higher level (DISCO-7609)
              const validFiles = files.filter(f => f.size > 0)
              if (validFiles.length > 0 && targetGroups.length > 0) {
                dispatch(uploadFilesToGroups(validFiles, targetGroups))
              }
            })
            return
          }

          if (enableGroupDrop && draggedItem.group) {
            if (
              !draggedItem.group.parent &&
              draggedItem.group.id !== group.id
            ) {
              const sourceIndex = userSortedChannels.findIndex(
                g => g.id === draggedItem.group!.id,
              )
              const targetIndex = userSortedChannels.findIndex(
                g => g.id === group.id,
              )
              const result = moveFromTo(
                userSortedChannels,
                sourceIndex,
                targetIndex,
              )
              dispatch(saveUserSortedChannels(result))
              return
            }

            if (draggedItem.group.parent === group.parent) {
              return
            }

            dispatch(
              updateGroup(
                {
                  ...draggedItem.group,
                  image:
                    draggedItem.image === null ? undefined : draggedItem.image,
                  parent: group.id,
                },
                true,
              ),
            )
          }

          dispatch(resetSelection())

          const eventName = draggedItem?.isPartOfMultipleSelection
            ? 'Drag and drop selected items'
            : 'Drag and drop existing track'

          mixpanel.sendPureEvent(
            eventName,
            getMixpanelDragAndDropProps(draggedItem, 'Channel'),
          )
        })()
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        draggedItem: monitor.getItem(),
      }),
    }),
    [
      dispatch,
      group,
      groups,
      selectedItems,
      userSortedChannels,
      nestingEnabled,
      lock_structure,
      lock_content,
      enableGroupDrop,
    ],
  )

  const [wasOver, setWasOver] = useState(isOver)

  useEffect(() => {
    setWasOver(isOver)
  }, [isOver])

  useTimeout(
    () => {
      if (isOver && canDrop && wasOver && onFileHover) {
        onFileHover()
      }
    },
    wasOver ? 1000 : null,
  )

  const isChannel = draggedItem?.group && !draggedItem.group.parent
  const isReordering = isOver && isChannel && !group.parent

  const [firstChild, ...rest] = React.Children.toArray(children)
  const wrappedFirstChild = React.cloneElement(
    firstChild as React.ReactElement<{
      isOver?: boolean
      isReordering?: boolean
    }>,
    {
      isOver: !isChannel && !isReordering && isOver && canDrop,
      isReordering,
    },
  )

  return (
    <div
      ref={dndConnectRef(dropRef)}
      className={cn({
        'is-over': isOver && canDrop,
        'is-track': draggedItem && isOver && draggedItem.track,
        'is-playlist': draggedItem && isOver && draggedItem.playlist,
        'is-group': draggedItem && isOver && draggedItem.group,
      })}
    >
      {wrappedFirstChild}
      {rest}
      {isOver &&
        canDrop &&
        draggedItem?.files &&
        appNode &&
        ReactDOM.createPortal(<DropFilesIndicator />, appNode)}
    </div>
  )
}
